/* No player SDK or video request until an explicit play action. */
if (!customElements.get('bilona-process-video')) {
  customElements.define('bilona-process-video', class extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.cleanup = new AbortController();
      const { signal } = this.cleanup;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.media = this.querySelector('.pv__media');
      this.cover = this.querySelector('.pv__cover');
      this.player = this.querySelector('.pv__player');
      this.closeButton = this.querySelector('[data-close]');
      this.status = this.querySelector('.pv__status');
      this.clickAnimations = new Set();
      this.addEventListener('click', event => {
        const target = event.target.closest('button, a');
        if (!target || !this.contains(target) || target.disabled) return;
        this.feedback(target, event);
        if (target.hasAttribute('data-play')) this.openVideo(target);
        if (target.hasAttribute('data-close')) this.closeVideo();
      }, { signal });
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && !this.player.hidden) {
          event.preventDefault();
          this.closeVideo();
        }
      }, { signal });
      if (this.classList.contains('pv--motion') && !this.reducedMotion.matches) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('pv__revealed');
            this.observer.unobserve(entry.target);
          });
        }, { threshold: .12 });
        this.querySelectorAll('.pv__intro,.pv__feature,.pv__media,.pv__footer').forEach(node => this.observer.observe(node));
      }
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.player.querySelector('video')?.pause();
      }, { signal });
    }
    openVideo(trigger) {
      const template = this.querySelector('template[data-player]');
      if (!template) return;
      this.returnFocus = trigger;
      if (this.player.hidden) {
        this.player.replaceChildren(template.content.cloneNode(true));
        this.cover.hidden = true;
        this.player.hidden = false;
        this.closeButton.hidden = false;
        this.media.classList.add('pv__media--playing');
      }
      const video = this.player.querySelector('video');
      if (video) {
        video.setAttribute('aria-label', this.getAttribute('aria-label') || 'Process video');
        video.addEventListener('error', () => {
          this.status.textContent = 'This video could not be loaded. Please try again.';
        }, { once: true, signal: this.cleanup.signal });
        const playing = video.play();
        playing?.catch(() => {
          if (this.isConnected && !this.player.hidden) this.status.textContent = 'Use the video controls to start playback.';
        });
        video.focus({ preventScroll: true });
      } else {
        this.player.querySelector('iframe')?.focus({ preventScroll: true });
      }
      if (trigger.closest('.pv__footer')) {
        this.media.scrollIntoView({ block: 'center', behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
      }
    }
    closeVideo(restoreFocus = true) {
      this.player?.querySelector('video')?.pause();
      this.player?.replaceChildren();
      if (this.player) this.player.hidden = true;
      if (this.cover) this.cover.hidden = false;
      if (this.closeButton) this.closeButton.hidden = true;
      this.media?.classList.remove('pv__media--playing');
      if (this.status) this.status.textContent = '';
      if (restoreFocus && this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
    }
    feedback(target, event) {
      if (!this.classList.contains('pv--motion') || this.reducedMotion.matches || target.classList.contains('pv__quote')) return;
      const rect = target.getBoundingClientRect();
      const wave = document.createElement('span');
      wave.className = 'pv__click-wave';
      wave.setAttribute('aria-hidden', 'true');
      wave.style.left = `${event.detail ? event.clientX - rect.left : rect.width / 2}px`;
      wave.style.top = `${event.detail ? event.clientY - rect.top : rect.height / 2}px`;
      target.append(wave);
      const animation = wave.animate([
        { transform: 'translate(-50%, -50%) scale(0)', opacity: .6 },
        { transform: `translate(-50%, -50%) scale(${Math.hypot(rect.width, rect.height) / 20})`, opacity: 0 }
      ], { duration: 450, easing: 'ease-out' });
      this.clickAnimations.add(animation);
      const finish = () => { wave.remove(); this.clickAnimations.delete(animation); };
      animation.finished.then(finish, finish);
    }
    disconnectedCallback() {
      this.cleanup?.abort();
      this.observer?.disconnect();
      this.clickAnimations?.forEach(animation => animation.cancel());
      this.closeVideo(false);
    }
  });
}
